/**
 * Two-Factor Authentication API Service
 */

import axiosInstance from './axiosInstance';
import { tokenStore } from '../tokenStore';
import { IAuthResponse, IUser } from '../../types/auth.types';

export interface ITwoFactorStatus {
  enabled: boolean;
  backupCodesRemaining: number;
  enabledAt: string | null;
}

export interface ITwoFactorSetupResponse {
  qrCodeDataUrl: string;
  manualEntryKey: string;
  issuer: string;
}

export interface ITwoFactorEnableResponse {
  enabled: boolean;
  backupCodes: string[];
  message: string;
}

export interface ITwoFactorLoginChallengeResponse extends IAuthResponse {
  user: IUser;
}

class TwoFactorService {
  private readonly baseUrl = '/auth/2fa';

  async getStatus(): Promise<ITwoFactorStatus> {
    const { data } = await axiosInstance.get<ITwoFactorStatus>(
      `${this.baseUrl}/status`,
    );
    return data;
  }

  async setup(): Promise<ITwoFactorSetupResponse> {
    const { data } = await axiosInstance.post<ITwoFactorSetupResponse>(
      `${this.baseUrl}/setup`,
    );
    return data;
  }

  async enable(code: string): Promise<ITwoFactorEnableResponse> {
    const { data } = await axiosInstance.post<ITwoFactorEnableResponse>(
      `${this.baseUrl}/enable`,
      { code },
    );
    return data;
  }

  async disable(password: string, code: string): Promise<{ enabled: boolean; message: string }> {
    const { data } = await axiosInstance.post(`${this.baseUrl}/disable`, {
      password,
      code,
    });
    return data;
  }

  async regenerateBackupCodes(code: string): Promise<{ backupCodes: string[]; message: string }> {
    const { data } = await axiosInstance.post(`${this.baseUrl}/backup-codes/regenerate`, {
      code,
    });
    return data;
  }

  async verifyLogin(
    twoFactorToken: string,
    code: string,
  ): Promise<ITwoFactorLoginChallengeResponse> {
    const { data } = await axiosInstance.post<ITwoFactorLoginChallengeResponse>(
      `${this.baseUrl}/verify-login`,
      { twoFactorToken, code },
    );

    if (data.access_token) {
      tokenStore.setAccessToken(data.access_token);
    }

    return data;
  }
}

export const twoFactorService = new TwoFactorService();
